/* global Alpine, Stripe */

/**
 * Componente Alpine per il pagamento sulla pagina dettaglio prenotazione.
 * Usa Stripe Payment Element per supportare carta, Google Pay, Apple Pay, ecc.
 * Montato solo quando la prenotazione è in stato unpaid/partially_paid.
 */
export default function bookingPayment() {
  document.addEventListener('alpine:init', () => {
    Alpine.data('bookingPayment', () => ({
      config: {},
      stripe: null,
      elements: null,
      paymentElement: null,

      // loading | ready | submitting | success | error
      status: 'loading',
      error: null,
      clientSecret: null,

      init() {
        const raw = this.$el.getAttribute('data-payment-config')
        if (raw) {
          try {
            this.config = JSON.parse(raw)
          } catch {
            this.config = {}
          }
        }

        this.initPayment()
      },

      async initPayment() {
        const strings = this.config.strings || {}

        try {
          if (typeof Stripe === 'undefined') {
            this.error = strings.loadingError || 'Stripe non disponibile.'
            this.status = 'error'
            return
          }

          const payUrl =
            this.config.payBookingEndpoint || '/api?endpoint=pay-booking'
          const res = await fetch(payUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              booking_sid: this.config.bookingSid || '',
            }),
          })

          const data = await res.json()
          if (!res.ok || data.error) {
            this.error = data.error || strings.loadingError || 'Errore.'
            this.status = 'error'
            return
          }

          const stripeData = data.result.stripe || {}
          const pk = stripeData.publishable_key
          if (!pk) {
            this.error = strings.loadingError || 'Stripe non disponibile.'
            this.status = 'error'
            return
          }

          this.stripe = Stripe(pk)
          this.clientSecret = stripeData.client_secret

          this.elements = this.stripe.elements({
            clientSecret: this.clientSecret,
            locale: document.documentElement.lang || 'it',
            appearance: {
              theme: 'stripe',
              variables: {
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                colorPrimary: '#3D9BF9',
                borderRadius: '8px',
              },
            },
          })

          this.paymentElement = this.elements.create('payment', {
            layout: 'tabs',
          })

          this.$nextTick(() => {
            const mountEl = document.getElementById('stripe-payment-element')
            if (mountEl) {
              this.paymentElement.mount(mountEl)
            }
          })

          this.paymentElement.on('ready', () => {
            this.status = 'ready'
          })

          this.paymentElement.on('change', (event) => {
            if (event.error) {
              this.error = event.error.message
            } else {
              this.error = null
            }
          })
        } catch {
          this.error = strings.loadingError || 'Errore di caricamento.'
          this.status = 'error'
        }
      },

      async submitPayment() {
        if (this.status === 'submitting') return

        const invStore = Alpine.store('bookingInvoice')
        if (invStore && invStore.blocksPayment) {
          this.error =
            invStore.paymentBlockMessage ||
            'Salva i dati di fatturazione prima di pagare.'
          const invoiceEl = document.getElementById('booking-invoice-card')
          if (invoiceEl) {
            invoiceEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
          return
        }

        this.error = null
        this.status = 'submitting'
        const strings = this.config.strings || {}

        try {
          const { error } = await this.stripe.confirmPayment({
            elements: this.elements,
            confirmParams: {
              payment_method_data: {
                billing_details: {
                  name: this.config.contactName || '',
                  email: this.config.contactEmail || '',
                },
              },
            },
            redirect: 'if_required',
          })

          if (error) {
            const t = error.type
            const isUserError = t === 'card_error' || t === 'validation_error'
            const fallback = strings.paymentDeclined || 'Pagamento rifiutato.'
            this.error = isUserError ? error.message : fallback
            this.status = 'ready'
            return
          }

          this.status = 'success'
          await this.pollAndReload()
        } catch {
          this.error = strings.paymentError || 'Errore pagamento.'
          this.status = 'ready'
        }
      },

      async pollAndReload() {
        const endpoint =
          this.config.bookingPaymentsEndpoint ||
          '/api?endpoint=booking-payments'
        const maxAttempts = 10
        const delayMs = 2000

        for (let i = 0; i < maxAttempts; i++) {
          await new Promise((r) => setTimeout(r, delayMs))
          try {
            const res = await fetch(endpoint, {
              headers: { Accept: 'application/json' },
            })
            const data = await res.json()
            const payments = data.result?.payments || []
            const succeeded = payments.some(
              (p) => p.boopay_status === 'succeeded'
            )
            if (succeeded) break
          } catch {
            // continua il polling
          }
        }

        window.location.reload()
      },
    }))
  })
}
